/**
 * CommandPalette Component - Quick search and navigation (Ctrl/Cmd+K)
 * Features: Fuzzy search, keyboard navigation, recent items
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Icons
function SearchIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function EventIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function SessionIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function NavigationIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ActionIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

// Quick navigation items
// roles: undefined = all users, array = only specified roles
const QUICK_ACTIONS = [
  { id: 'nav-dashboard', label: 'Go to Dashboard', path: '/organizer', type: 'navigation', icon: NavigationIcon, roles: ['organizer', 'admin'] },
  { id: 'nav-events', label: 'Browse Events', path: '/events', type: 'navigation', icon: NavigationIcon },
  { id: 'nav-schedule', label: 'My Schedule', path: '/schedule', type: 'navigation', icon: NavigationIcon },
  { id: 'nav-profile', label: 'Profile Settings', path: '/profile', type: 'navigation', icon: NavigationIcon },
  { id: 'action-create', label: 'Create New Event', path: '/events/create', type: 'action', icon: ActionIcon, roles: ['organizer', 'admin'] },
];

export default function CommandPalette({ isOpen, onClose }) {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentEvents, setRecentEvents] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Filter quick actions based on user role
  const quickActions = useMemo(() => {
    return QUICK_ACTIONS.filter(action => {
      if (!action.roles) return true; // No role restriction
      return user && action.roles.includes(user.role);
    });
  }, [user]);

  // Fetch recent events when opened
  useEffect(() => {
    if (isOpen) {
      fetchRecentEvents();
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const fetchRecentEvents = async () => {
    try {
      const response = await api.get('/organizer/dashboard');
      const events = response.data.data?.events || [];
      setRecentEvents(events.slice(0, 5).map(e => ({
        id: `event-${e.id}`,
        label: e.name,
        path: `/events/${e.id}/manage`,
        type: 'event',
        icon: EventIcon,
        subtitle: e.status,
      })));
    } catch (err) {
      setRecentEvents([]);
    }
  };

  // Search sessions and events
  const search = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const [eventsRes, sessionsRes] = await Promise.all([
        api.get(`/events?search=${encodeURIComponent(searchQuery)}&limit=5`).catch(() => ({ data: { data: [] } })),
        api.get(`/sessions/search?q=${encodeURIComponent(searchQuery)}&limit=5`).catch(() => ({ data: { data: [] } })),
      ]);

      const eventResults = (eventsRes.data.data || []).map(e => ({
        id: `event-${e.id}`,
        label: e.name,
        path: `/events/${e.slug || e.id}`,
        type: 'event',
        icon: EventIcon,
        subtitle: e.location,
      }));

      const sessionResults = (sessionsRes.data.data || []).map(s => ({
        id: `session-${s.id}`,
        label: s.title,
        path: `/events/${s.event_id}#session-${s.id}`,
        type: 'session',
        icon: SessionIcon,
        subtitle: s.tags?.[0]?.name || s.track || s.session_type,
      }));

      setResults([...eventResults, ...sessionResults]);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Filter quick actions based on query
  const filteredActions = query
    ? quickActions.filter(a => a.label.toLowerCase().includes(query.toLowerCase()))
    : quickActions;

  // Combined items
  const allItems = query
    ? [...results, ...filteredActions]
    : [...recentEvents, ...quickActions];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, allItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (allItems[selectedIndex]) {
            handleSelect(allItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, allItems, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.children[selectedIndex];
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleSelect = (item) => {
    navigate(item.path);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="command-palette-overlay" onClick={onClose}>
        <div className="command-palette" onClick={e => e.stopPropagation()}>
          <div className="command-input-wrapper">
            <SearchIcon />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search events, sessions, or type a command..."
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              className="command-input"
            />
            <kbd className="command-kbd">esc</kbd>
          </div>

          <div className="command-results" ref={listRef}>
            {loading && (
              <div className="command-loading">Searching...</div>
            )}

            {!loading && allItems.length === 0 && query && (
              <div className="command-empty">No results found for "{query}"</div>
            )}

            {!query && recentEvents.length > 0 && (
              <div className="command-section">
                <div className="command-section-title">Recent Events</div>
                {recentEvents.map((item, index) => (
                  <CommandItem
                    key={item.id}
                    item={item}
                    isSelected={index === selectedIndex}
                    onSelect={handleSelect}
                    onHover={() => setSelectedIndex(index)}
                  />
                ))}
              </div>
            )}

            {!query && quickActions.length > 0 && (
              <div className="command-section">
                <div className="command-section-title">Quick Actions</div>
                {quickActions.map((item, index) => (
                  <CommandItem
                    key={item.id}
                    item={item}
                    isSelected={index + recentEvents.length === selectedIndex}
                    onSelect={handleSelect}
                    onHover={() => setSelectedIndex(index + recentEvents.length)}
                  />
                ))}
              </div>
            )}

            {query && results.length > 0 && (
              <div className="command-section">
                <div className="command-section-title">Search Results</div>
                {results.map((item, index) => (
                  <CommandItem
                    key={item.id}
                    item={item}
                    isSelected={index === selectedIndex}
                    onSelect={handleSelect}
                    onHover={() => setSelectedIndex(index)}
                  />
                ))}
              </div>
            )}

            {query && filteredActions.length > 0 && (
              <div className="command-section">
                <div className="command-section-title">Actions</div>
                {filteredActions.map((item, index) => (
                  <CommandItem
                    key={item.id}
                    item={item}
                    isSelected={index + results.length === selectedIndex}
                    onSelect={handleSelect}
                    onHover={() => setSelectedIndex(index + results.length)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="command-footer">
            <span><kbd>↑↓</kbd> Navigate</span>
            <span><kbd>↵</kbd> Select</span>
            <span><kbd>esc</kbd> Close</span>
          </div>
        </div>
      </div>
    </>
  );
}

function CommandItem({ item, isSelected, onSelect, onHover }) {
  const Icon = item.icon;
  return (
    <button
      className={`command-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(item)}
      onMouseEnter={onHover}
    >
      <span className="command-item-icon">
        <Icon />
      </span>
      <span className="command-item-content">
        <span className="command-item-label">{item.label}</span>
        {item.subtitle && <span className="command-item-subtitle">{item.subtitle}</span>}
      </span>
      <span className={`command-item-type ${item.type}`}>{item.type}</span>
    </button>
  );
}

const styles = `
  .command-palette-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 15vh;
    z-index: 9999;
    animation: fadeIn 0.15s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .command-palette {
    width: 100%;
    max-width: 600px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
    animation: slideDown 0.2s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .command-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .command-input-wrapper svg {
    color: #9ca3af;
    flex-shrink: 0;
  }

  .command-input {
    flex: 1;
    background: transparent;
    border: none;
    color: #111827;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
  }

  .command-input::placeholder {
    color: #9ca3af;
  }

  .command-kbd {
    padding: 0.25rem 0.5rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-family: inherit;
    color: #6b7280;
    text-transform: uppercase;
  }

  .command-results {
    max-height: 400px;
    overflow-y: auto;
    padding: 0.5rem;
  }

  .command-loading,
  .command-empty {
    padding: 2rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .command-section {
    margin-bottom: 0.5rem;
  }

  .command-section-title {
    padding: 0.5rem 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #9ca3af;
  }

  .command-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem;
    background: transparent;
    border: none;
    border-radius: 0.5rem;
    color: #374151;
    font-family: inherit;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .command-item:hover,
  .command-item.selected {
    background: #f9fafb;
  }

  .command-item.selected {
    background: #fef2f3;
  }

  .command-item-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    color: #6b7280;
    flex-shrink: 0;
  }

  .command-item.selected .command-item-icon {
    border-color: #e94560;
    color: #e94560;
  }

  .command-item-content {
    flex: 1;
    min-width: 0;
  }

  .command-item-label {
    display: block;
    font-size: 0.9375rem;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .command-item-subtitle {
    display: block;
    font-size: 0.75rem;
    color: #9ca3af;
    margin-top: 0.125rem;
  }

  .command-item-type {
    padding: 0.125rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-radius: 9999px;
    flex-shrink: 0;
  }

  .command-item-type.event {
    background: #fef2f3;
    color: #e94560;
  }

  .command-item-type.session {
    background: #eff6ff;
    color: #3b82f6;
  }

  .command-item-type.navigation {
    background: #f0fdf4;
    color: #16a34a;
  }

  .command-item-type.action {
    background: #faf5ff;
    color: #9333ea;
  }

  .command-footer {
    display: flex;
    gap: 1.5rem;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #f3f4f6;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .command-footer kbd {
    padding: 0.125rem 0.375rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 3px;
    font-size: 0.625rem;
    font-family: inherit;
    margin-right: 0.375rem;
  }
`;
